import { useEffect, useState } from "react";
import type { DocumentPublic, DocumentCategory } from "@dugads/shared";
import { DOCUMENT_CATEGORIES } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  FileText,
  Download,
  Trash2,
  Pin,
  PinOff,
  Upload,
  Users,
} from "lucide-react";
import { DocumentUploadDialog } from "@/components/document-upload-dialog";

interface DocumentListProps {
  schoolId: string;
}

const CATEGORY_LABELS: Record<DocumentCategory, string> = {
  newsletter: "Newsletter",
  schedule: "Schedule",
  indemnity: "Indemnity",
  general: "General",
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentList({ schoolId }: DocumentListProps) {
  const [documents, setDocuments] = useState<DocumentPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [showUpload, setShowUpload] = useState(false);

  function loadDocuments() {
    setLoading(true);
    setError(null);
    api
      .getSchoolDocuments(schoolId, categoryFilter || undefined)
      .then((res) => setDocuments(res.documents))
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load documents"),
      )
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadDocuments();
  }, [schoolId, categoryFilter]);

  async function handleDelete(docId: string) {
    if (!confirm("Delete this document? This action cannot be undone.")) return;
    try {
      await api.deleteDocument(docId);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to delete document");
    }
  }

  async function handleTogglePin(docId: string) {
    try {
      const res = await api.toggleDocumentPin(docId);
      setDocuments((prev) =>
        prev.map((d) => (d.id === docId ? res.document : d)),
      );
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to toggle pin");
    }
  }

  async function handleDownload(docId: string, fileName: string) {
    try {
      const token = await import("@/lib/firebase").then((m) =>
        m.getFirebaseAuth()?.currentUser?.getIdToken(),
      );
      const url = api.getDocumentDownloadUrl(docId);
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      setError("Failed to download document");
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={categoryFilter === "" ? "default" : "outline"}
            size="sm"
            onClick={() => setCategoryFilter("")}
          >
            All
          </Button>
          {DOCUMENT_CATEGORIES.map((cat) => (
            <Button
              key={cat}
              variant={categoryFilter === cat ? "default" : "outline"}
              size="sm"
              onClick={() => setCategoryFilter(cat)}
            >
              {CATEGORY_LABELS[cat]}
            </Button>
          ))}
        </div>
        <Button size="sm" onClick={() => setShowUpload(true)}>
          <Upload className="mr-2 h-4 w-4" />
          Upload Document
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {loading && <p className="text-muted-foreground">Loading...</p>}

      {!loading && documents.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-12">
          <FileText className="h-8 w-8 text-muted-foreground" />
          <p className="text-muted-foreground">No documents yet</p>
          <Button variant="outline" size="sm" onClick={() => setShowUpload(true)}>
            Upload your first document
          </Button>
        </div>
      )}

      {!loading && documents.length > 0 && (
        <div className="rounded-lg border border-border divide-y divide-border">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30"
            >
              <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{doc.original_name}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5">
                    {CATEGORY_LABELS[doc.category]}
                  </span>
                  <span>{formatFileSize(doc.size_bytes)}</span>
                  <span>by {doc.uploader_name}</span>
                  <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                  {doc.is_pinned && (
                    <span className="inline-flex items-center gap-1 text-amber-600">
                      <Pin className="h-3 w-3" /> Pinned
                    </span>
                  )}
                  {doc.recipient_count > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3 w-3" /> {doc.recipient_count}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-0.5 shrink-0">
                {doc._actions.includes("download") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Download"
                    onClick={() => handleDownload(doc.id, doc.original_name)}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                )}
                {doc._actions.includes("pin") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    title={doc.is_pinned ? "Unpin" : "Pin"}
                    onClick={() => handleTogglePin(doc.id)}
                  >
                    {doc.is_pinned ? (
                      <PinOff className="h-3.5 w-3.5" />
                    ) : (
                      <Pin className="h-3.5 w-3.5" />
                    )}
                  </Button>
                )}
                {doc._actions.includes("delete") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Delete"
                    onClick={() => handleDelete(doc.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <DocumentUploadDialog
          schoolId={schoolId}
          onClose={() => setShowUpload(false)}
          onUploaded={() => {
            setShowUpload(false);
            loadDocuments();
          }}
        />
      )}
    </div>
  );
}
